import BaseAuthProvider from './BaseAuthProvider'
import AuthService from '../service'
import { AuthProviders, OAuthBackends, SocialProviders } from '../constants/authProviders'
import { loadInitAppleSDK } from '@/utils/social_oauth/apple'

/**
 * Apple ID Authentication Provider
 * Uses Apple ID JS SDK
 */
export class AppleAuthProvider extends BaseAuthProvider {
    constructor() {
        super()
        this._sdkLoaded = false
    }

    getMethod() {
        return AuthProviders.APPLE
    }

    /**
     * Apple login has custom restrictions (same as other social):
     * 1. Must be in .env providers
     * 2. Can be disabled by backend (appConfig.disable_social_login)
     * 3. Can be disabled for specific subdomains (e.g., 'photos')
     * 4. Requires Apple App ID in environment
     */
    isEnabled(context) {
        // 1. Check .env (base requirement)
        if (!context.envProviders.includes(this.getMethod())) {
            return false
        }

        // 2. Check backend override (can only disable)
        if (context.appConfig && context.appConfig.disable_social_login) {
            return false
        }

        // 3. Check hardcoded subdomain rules
        // CUSTOM LOGIC: 'photos' subdomain disables all social login
        if (context.subdomain === 'photos') {
            return false
        }

        // 4. Check if email is prefilled from external source (preview JWT, etc.)
        // When email is prefilled, user must login with that specific email
        // Social login is disabled to prevent wrong account usage
        if (context.contextParams && context.contextParams.email) {
            return false
        }

        // 5. Check required credentials
        if (!process.env.VUE_APP_APPLE_APP_ID) {
            console.warn('Apple provider enabled but VUE_APP_APPLE_APP_ID not configured')
            return false
        }

        return true
    }

    /**
     * Load Apple SDK if not already loaded
     * @returns {Promise<void>}
     */
    async loadSDK() {
        if (this._sdkLoaded) return

        loadInitAppleSDK()
        this._sdkLoaded = true

        // Wait for SDK to load
        await new Promise(resolve => setTimeout(resolve, 1000))
    }

    /**
     * Start Apple ID login flow
     * @returns {Promise<string>} Apple ID token
     */
    async startLogin() {
        await this.loadSDK()

        const response = await window.AppleID.auth.signIn()
        // Apple returns id_token instead of access_token
        return response.authorization.id_token
    }

    /**
     * Authenticate with Apple ID token
     * @param {Object} credentials
     * @param {string} credentials.token - Apple ID token (optional, will prompt if not provided)
     * @param {boolean} credentials.allowRegister - Allow new user registration
     * @returns {Promise<{token: string, tokenType: string}>}
     */
    async authenticate({ token, allowRegister = false }) {
        // If no token provided, start OAuth flow
        if (!token) {
            token = await this.startLogin()
        }

        // Convert Apple token to app token
        const response = await AuthService.convertAuthToken({
            backend: OAuthBackends[AuthProviders.APPLE],
            token: token,
            grant_type: 'convert_token',
            client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
            allow_register: allowRegister
        })

        // Return token plus any additional metadata from backend
        return {
            token: response.data.access_token,
            tokenType: 'Bearer',
            ...response.data  // Include metadata if backend adds any in future
        }
    }

    /**
     * Suggest alternatives - other social providers or password
     */
    getAlternatives(enabledProviders) {
        const alternatives = []

        // Other social providers
        const otherSocial = SocialProviders.filter(p =>
            p !== this.getMethod() && enabledProviders.includes(p)
        )
        alternatives.push(...otherSocial)

        // Password login
        if (enabledProviders.includes(AuthProviders.PASSWORD)) {
            alternatives.push(AuthProviders.PASSWORD)
        }

        return alternatives
    }
}

export default AppleAuthProvider
